import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Mail, ShieldCheck } from 'lucide-react';
import { Button, buttonClass } from '@/components/ui/Button';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useI18n } from '@/hooks/useI18n';
import { useSesion } from './identidad/useSesion';
import { useMensajeDeAcceso } from './identidad/useMensajeDeAcceso';
import { RUTA_ACCESO } from './rutas';
import styles from './area.module.css';

/**
 * Cambiar la contraseña sin saber la anterior: se pide un código y se canjea.
 *
 * ## Dos pasos en una pantalla, y no dos rutas
 *
 * Porque el segundo paso necesita el correo del primero. Con dos rutas habría
 * que pasarlo por la URL —y un correo en una URL acaba en el historial, en el
 * `Referer` y en los logs de cualquier proxy— o guardarlo en `sessionStorage`,
 * que es estado a mano para no perder una variable.
 *
 * El precio: recargar vuelve al paso 1. Es aceptable porque el código sigue
 * siendo válido y se puede volver a pedir; y quien recarga a mitad de esto casi
 * siempre lo hace porque algo no le cuadró.
 *
 * ## Lo que esta pantalla NO puede prometer
 *
 * El servidor contesta **lo mismo** exista o no la cuenta —es lo que impide que
 * esto sea un enumerador de correos— así que aquí no se sabe si el correo salió.
 * El copy lo dice con esas palabras: «si ese correo está dado de alta».
 *
 * Y hay una razón de más para decirlo así, medida el 2026-09-21: **las cuentas
 * están en el sandbox de SES**, que entrega sólo a direcciones verificadas. Hoy
 * el caso normal es que no llegue. Un «te mandamos un código» a secas sería
 * sencillamente falso.
 */
export function RecuperarContrasenaPage() {
  const { t } = useI18n();
  const r = t.area.recuperar;
  const a = t.area.acceso;
  const mensaje = useMensajeDeAcceso();
  const { pedirCodigo, restablecer } = useSesion();

  useDocumentMeta(t.meta.recuperar);

  const [paso, setPaso] = useState<1 | 2 | 3>(1);
  const [correo, setCorreo] = useState('');
  const [codigo, setCodigo] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [verContrasena, setVerContrasena] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function pedir(evento: FormEvent) {
    evento.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      await pedirCodigo(correo);
      setPaso(2);
    } catch (e) {
      setError(mensaje(e));
    } finally {
      setEnviando(false);
    }
  }

  async function canjear(evento: FormEvent) {
    evento.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      await restablecer(correo, codigo, contrasena);
      setPaso(3);
    } catch (e) {
      setError(mensaje(e));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className={styles.page}>
      <div className="ilu-container">
        <header className={styles.head}>
          <h1 className={styles.title}>{r.titulo}</h1>
          <p className={styles.subtitle}>{r.subtitulo}</p>
        </header>

        <div className={[styles.card, styles.puerta].join(' ')}>
          {error && (
            <p className={[styles.aviso, styles.avisoError].join(' ')} role="alert">
              {error}
            </p>
          )}

          {paso === 1 && (
            <form onSubmit={(e) => void pedir(e)} noValidate>
              <p className={styles.metricHint} style={{ marginBottom: 20 }}>
                {r.paso1.explicacion}
              </p>
              <div className={styles.field}>
                <label htmlFor="rec-correo">{a.correo}</label>
                <input
                  id="rec-correo"
                  type="email"
                  autoComplete="username"
                  required
                  value={correo}
                  onChange={(e) => setCorreo(e.target.value)}
                />
              </div>
              <div className={styles.acciones}>
                <Button type="submit" disabled={enviando || !correo.trim()}>
                  {enviando ? r.paso1.enviando : r.paso1.cta}
                </Button>
                <Link to={RUTA_ACCESO} className={buttonClass({ variant: 'ghost' })}>
                  {r.volver}
                </Link>
              </div>
            </form>
          )}

          {paso === 2 && (
            <form onSubmit={(e) => void canjear(e)} noValidate>
              {/*
                `role="status"` y no `alert`: es informativo, no interrumpe. Y el
                texto es condicional a propósito —«si ese correo está dado de
                alta»— porque el servidor no dice si existe y aquí no se puede
                afirmar más de lo que se sabe.
              */}
              <p className={[styles.aviso, styles.avisoNeutro].join(' ')} role="status">
                <Mail size={16} aria-hidden="true" style={{ verticalAlign: '-3px' }} />{' '}
                {r.paso2.avisoAntes}
                <strong>{correo}</strong>
                {r.paso2.avisoDespues}
              </p>

              <div className={styles.field}>
                <label htmlFor="rec-codigo">{r.paso2.codigo}</label>
                {/*
                  `inputMode` no, `autoCapitalize` en `characters` sí: el código
                  es alfanumérico en mayúsculas, y en un móvil el teclado empieza
                  en minúscula. El servidor normaliza igual —quita guiones y
                  sube a mayúsculas— así que esto es comodidad, no validación.
                */}
                <input
                  id="rec-codigo"
                  type="text"
                  required
                  autoComplete="one-time-code"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder={r.paso2.ejemplo}
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value)}
                />
              </div>

              <div className={[styles.field, styles.campoConOjo].join(' ')}>
                <label htmlFor="rec-nueva">{r.paso2.nueva}</label>
                <input
                  id="rec-nueva"
                  type={verContrasena ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  spellCheck={false}
                  autoCorrect="off"
                  autoCapitalize="off"
                  value={contrasena}
                  onChange={(e) => setContrasena(e.target.value)}
                  aria-describedby="rec-nueva-pista"
                />
                <button
                  type="button"
                  className={styles.ojo}
                  onClick={() => setVerContrasena((v) => !v)}
                  aria-label={verContrasena ? a.ocultarContrasena : a.mostrarContrasena}
                  aria-controls="rec-nueva"
                >
                  {verContrasena ? (
                    <EyeOff size={18} aria-hidden="true" />
                  ) : (
                    <Eye size={18} aria-hidden="true" />
                  )}
                </button>
                {/*
                  El mínimo se dice ANTES de escribir, no después de fallar. Un
                  formulario que sólo cuenta sus reglas cuando las incumples es
                  un formulario que hace adivinar.
                */}
                <p className={styles.metricHint} id="rec-nueva-pista" style={{ marginTop: 6 }}>
                  {r.paso2.minimo}
                </p>
              </div>

              <div className={styles.acciones}>
                <Button type="submit" disabled={enviando || !codigo.trim() || !contrasena}>
                  {enviando ? r.paso2.enviando : r.paso2.cta}
                </Button>
                <button
                  type="button"
                  className={buttonClass({ variant: 'ghost' })}
                  onClick={() => {
                    setPaso(1);
                    setCodigo('');
                    setError(null);
                  }}
                >
                  {r.paso2.otroCodigo}
                </button>
              </div>
            </form>
          )}

          {paso === 3 && (
            <>
              <p className={[styles.aviso, styles.avisoNeutro].join(' ')} role="status">
                <ShieldCheck size={16} aria-hidden="true" style={{ verticalAlign: '-3px' }} />{' '}
                {r.listo}
              </p>
              <Link to={RUTA_ACCESO} className={buttonClass({ variant: 'primary' })}>
                {r.entrar}
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
